import PropTypes from "prop-types";
import { memo, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import { getFriendlyFileType } from "../utils/fileTypeUtils";
import FileAttachment from "./FileAttachment";

// Production FileAttachments component with optimized rendering

// Global cache for normalized file objects (stable references)
const normalizedFileCache = new Map();

/**
 * Normalize file object to create stable structure
 * This prevents reference changes during streaming
 */
function normalizeFile(file, messageId) {
  const cacheKey = `${messageId}-${file?.id || file?.path || file?.name || "file"}`;

  if (normalizedFileCache.has(cacheKey)) {
    return normalizedFileCache.get(cacheKey);
  }

  // Create normalized file structure
  // CRITICAL: Preserve ALL original fields to ensure download/display works correctly
  const normalized = {
    // Spread all original fields first
    ...file,
    // Then override with normalized/computed fields
    id: file?.id || file?.file_id || null,
    path: file?.path || file?.name || "",
    name: file?.name || file?.path?.split("/").pop() || "Unknown",
    type: file?.type || file?.path?.split(".").pop()?.toLowerCase() || "file",
    isImage:
      file?.isImage ??
      (file?.type?.startsWith("image/") ||
        /\.(jpg|jpeg|png|gif|webp|bmp|svg|ico)$/i.test(
          file?.name || file?.path || ""
        )),
    friendlyType: getFriendlyFileType(
      file?.type || file?.path?.split(".").pop()?.toLowerCase() || "file",
      file?.name || file?.path?.split("/").pop()
    ),
    // Store original for reference
    _original: file,
  };

  normalizedFileCache.set(cacheKey, normalized);
  return normalized;
}

/**
 * Stable Image Component - isolated with React.memo to prevent re-renders
 */
const StableImageAttachment = memo(
  function StableImageAttachment({ file, messageId, threadId }) {
    const renderCountRef = useRef(0);

    useEffect(() => {
      renderCountRef.current += 1;
      if (import.meta.env.DEV && renderCountRef.current % 10 === 0) {
        // Only log every 10th render to avoid spam
        console.log(
          `[StableImageAttachment] Render #${renderCountRef.current} for ${file?.name || "image"}`
        );
      }
    });

    return (
      <FileAttachment
        key={`${messageId}-${file?.id || file?.path || file?.name || "img"}`}
        file={file}
        isImage={true}
        messageId={messageId}
        threadId={threadId}
      />
    );
  },
  (prevProps, nextProps) => {
    // Strict comparison - only re-render if file actually changed
    const prevKey = `${prevProps.messageId}-${prevProps.file?.id || prevProps.file?.path || prevProps.file?.name}`;
    const nextKey = `${nextProps.messageId}-${nextProps.file?.id || nextProps.file?.path || nextProps.file?.name}`;

    if (prevKey !== nextKey) return false;

    // Compare file properties that matter
    if (prevProps.file?.path !== nextProps.file?.path) return false;
    if (prevProps.file?.name !== nextProps.file?.name) return false;
    if (prevProps.file?.id !== nextProps.file?.id) return false;

    // If everything matches, skip render (stable)
    return true;
  }
);

StableImageAttachment.displayName = "StableImageAttachment";

/**
 * Stable File Component - for non-image files
 */
const StableFileAttachment = memo(
  function StableFileAttachment({ file, messageId }) {
    return (
      <FileAttachment
        key={`${messageId}-${file?.id || file?.path || file?.name || "file"}`}
        file={file}
        isImage={false}
      />
    );
  },
  (prevProps, nextProps) => {
    const prevKey = `${prevProps.messageId}-${prevProps.file?.id || prevProps.file?.path}`;
    const nextKey = `${nextProps.messageId}-${nextProps.file?.id || nextProps.file?.path}`;
    return (
      prevKey === nextKey &&
      prevProps.file?.path === nextProps.file?.path &&
      prevProps.file?.name === nextProps.file?.name
    );
  }
);

StableFileAttachment.displayName = "StableFileAttachment";

/**
 * File Attachments Component - Production version with optimized rendering
 *
 * Key improvements to prevent image flickering:
 * - Strong memoization that prevents re-renders during streaming
 * - Normalized file objects stored in cache (stable references)
 * - Isolated image components with strict memo comparison
 * - Files only recalculated when file data actually changes
 */
const PureFileAttachments = ({
  files = [],
  messageId,
  threadId,
  isUser = false,
  className = "",
}) => {
  const renderCountRef = useRef(0);
  const processedFilesRef = useRef(null);
  const lastFilesHashRef = useRef(null);

  // Debug render tracking
  useEffect(() => {
    renderCountRef.current += 1;
    if (import.meta.env.DEV) {
      console.log(`[FileAttachments] Render #${renderCountRef.current}`, {
        messageId,
        fileCount: files?.length || 0,
      });
    }
  }, [messageId, files?.length]);

  // CRITICAL: Normalize files once and cache them
  // Only recalculate if files actually change (not on every render)
  const processedFiles = useMemo(() => {
    if (!files || files.length === 0) {
      return { imageFiles: [], regularFiles: [] };
    }

    // Create hash of files to detect changes
    const filesHash = JSON.stringify(
      files.map((f) => ({
        id: f?.id,
        path: f?.path,
        name: f?.name,
        type: f?.type,
      }))
    );

    // If files haven't changed, return cached processed files
    if (lastFilesHashRef.current === filesHash && processedFilesRef.current) {
      if (import.meta.env.DEV && renderCountRef.current % 10 === 0) {
        console.log(
          `[FileAttachments] Using cached processed files for ${messageId}`
        );
      }
      return processedFilesRef.current;
    }

    // Files changed - process them
    lastFilesHashRef.current = filesHash;

    // Normalize all files (creates stable references)
    const normalizedFiles = files
      .map((file) => normalizeFile(file, messageId))
      .filter((file) => file && (file.name || file.path));

    // Separate images from regular files
    const imageFiles = normalizedFiles.filter((file) => file.isImage);
    const regularFiles = normalizedFiles.filter((file) => !file.isImage);

    const result = { imageFiles, regularFiles };
    processedFilesRef.current = result;

    if (import.meta.env.DEV) {
      console.log(`[FileAttachments] Processed files for ${messageId}`, {
        imageCount: imageFiles.length,
        fileCount: regularFiles.length,
      });
    }

    return result;
  }, [
    // CRITICAL: Only depend on file identifiers, not full file objects
    messageId,
    files
      ?.map((f) => `${f?.id || ""}-${f?.path || ""}-${f?.name || ""}`)
      .join("|"),
  ]);

  // Clean up cache when component unmounts or messageId changes
  useEffect(() => {
    return () => {
      // Optionally clean up cache entries for this messageId
      // But keep them for a bit in case of re-renders
      // The cache will be managed globally
    };
  }, [messageId]);

  if (
    !processedFiles ||
    (!processedFiles.imageFiles?.length && !processedFiles.regularFiles?.length)
  ) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex flex-col space-y-1",
        isUser ? "items-end" : "items-start",
        className
      )}
      // Stop event propagation to prevent parent re-renders from affecting this
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseLeave={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Images displayed in a grid */}
      {processedFiles.imageFiles.length > 0 && (
        <div
          className={cn(
            "grid gap-1.5",
            processedFiles.imageFiles.length > 1
              ? "grid-cols-2"
              : "grid-cols-1",
            isUser ? "w-fit ml-auto" : "w-full"
          )}
        >
          {processedFiles.imageFiles.map((file, index) => (
            <StableImageAttachment
              key={`${messageId}-img-${file.id || file.path || file.name || index}`}
              file={file}
              messageId={messageId}
              threadId={threadId}
            />
          ))}
        </div>
      )}

      {/* Regular files listed vertically */}
      {processedFiles.regularFiles.length > 0 && (
        <div className="space-y-0.5 w-fit">
          {processedFiles.regularFiles.map((file, index) => (
            <StableFileAttachment
              key={`${messageId}-file-${file.id || file.path || file.name || index}`}
              file={file}
              messageId={messageId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Main FileAttachments component with strong memoization
 */
export const FileAttachments = memo(
  PureFileAttachments,
  (prevProps, nextProps) => {
    // CRITICAL: Strict comparison to prevent re-renders during streaming

    // Different message ID means definitely different files
    if (prevProps.messageId !== nextProps.messageId) {
      return false;
    }

    // Different isUser value means different alignment
    if (prevProps.isUser !== nextProps.isUser) {
      return false;
    }

    // If file arrays have different lengths, re-render
    if (prevProps.files?.length !== nextProps.files?.length) {
      return false;
    }

    // If no files, skip comparison
    if (!prevProps.files?.length && !nextProps.files?.length) {
      return true;
    }

    // Deep comparison of file identifiers (not full objects)
    // Only check what actually matters for file identity
    const prevFileKeys = (prevProps.files || [])
      .map((f) => `${f?.id || ""}-${f?.path || ""}-${f?.name || ""}`)
      .join("|");
    const nextFileKeys = (nextProps.files || [])
      .map((f) => `${f?.id || ""}-${f?.path || ""}-${f?.name || ""}`)
      .join("|");

    if (prevFileKeys !== nextFileKeys) {
      return false;
    }

    // CRITICAL: If file keys match, skip render (files haven't changed)
    // This is the key fix - during streaming, message objects change but files don't
    // By comparing only file identifiers, we prevent unnecessary re-renders
    return true;
  }
);

FileAttachments.propTypes = {
  files: PropTypes.array,
  messageId: PropTypes.string.isRequired,
  threadId: PropTypes.string,
  isUser: PropTypes.bool,
};

FileAttachments.displayName = "FileAttachments";

/**
 * Static File Attachments - Non-memoized version for use in skeletons and static rendering
 * This is exported for use in MessageSkeletons where we want direct rendering without memoization
 */
export const StaticFileAttachments = PureFileAttachments;

StaticFileAttachments.displayName = "StaticFileAttachments";

export default FileAttachments;
